// apps/roleforge-app/src/components/audit/AuditConverseColumn.tsx
//
// Middle column. Defaults to the chat panel; renders the AssetEditorPane
// instead when an asset is opened from the right column. Renders the
// ActivationMomentLayout when audit is locked.
//
// Adapted from AAB's CoachWorkspace, but XOR (chat OR asset) rather than
// AND (chat AND asset side-by-side).

import { type AuditRow, type ChangeProposal, type Message, type RoleforgeAgentProfile } from '../../lib/api-client';
import type { SseEventRouter } from '../../lib/sse-event-router';
import type { ActivityItem } from '../../lib/use-activity-stream';
import type { OperatingModelState } from '../../lib/use-operating-model-state';
import { ChatTranscript } from '../chat/ChatTranscript';
import { ChatComposer } from '../chat/ChatComposer';
import { ForgeSynthesisBurst } from '../forge/ForgeSynthesisBurst';
import { ActivationMomentLayout, type ActivationMomentLayoutProps } from './ActivationMomentLayout';
import { AssetEditorPane } from './AssetEditorPane';
import { AttributedAssetsPane } from './AttributedAssetsPane';
import { useValidationLockIn, ValidationLockInAction } from './ValidationLockInAction';
import type { AssetId } from './AssetsColumn';
import type { ShellMode } from './AuditPersistentShell';

interface Props {
  audit: AuditRow;
  jwt?: string | null;
  router: SseEventRouter;
  refreshAudit: () => Promise<void>;
  mode: ShellMode;
  messages: Message[];
  agentProfile?: RoleforgeAgentProfile | null;
  streamingAgentText: string;
  activityItems: ActivityItem[];
  onSend?: (text: string) => void;
  composerDisabled?: boolean;
  composerPlaceholder?: string;
  activationData?: ActivationMomentLayoutProps;
  changeProposals?: ChangeProposal[];
  onApproveProposal?: (proposalId: string, mode: 'later' | 'now') => Promise<void>;
  activeAssetId: AssetId | null;
  onCloseAsset: () => void;
  operatingModelState: OperatingModelState;
  /** Forge-fork synthesis burst overlay state. */
  synthesisBurstActive: boolean;
  onSynthesisBurstDone: () => void;
  /**
   * Phase 11 — when the owner clicks a voice in the Voices tab, the
   * `activeAssetId` becomes `voice:<threadId>` and we render the
   * AttributedAssetsPane instead of the chat. We need the human-readable
   * label to title that pane; resolved by AuditPersistentShell from its
   * threads list.
   */
  resolveVoiceLabel?: (threadId: string) => string | undefined;
}

export function AuditConverseColumn({
  audit,
  jwt,
  router,
  refreshAudit,
  mode,
  messages,
  agentProfile,
  streamingAgentText,
  activityItems,
  onSend,
  composerDisabled,
  composerPlaceholder,
  activationData,
  changeProposals = [],
  onApproveProposal,
  activeAssetId,
  onCloseAsset,
  operatingModelState,
  synthesisBurstActive,
  onSynthesisBurstDone,
  resolveVoiceLabel,
}: Props) {
  // Keep the lock transition above the XOR chat-or-asset branches. Switching
  // surfaces must not mount a fresh enabled action while the first request is
  // still unresolved.
  const validationLockIn = useValidationLockIn({ audit, jwt, refreshAudit });

  // Phase 11 — voice contributions take priority: when the owner clicks a
  // voice in the Voices tab, the middle column becomes the AttributedAssetsPane.
  if (activeAssetId && activeAssetId.startsWith('voice:')) {
    const threadId = activeAssetId.slice('voice:'.length);
    const voiceLabel = resolveVoiceLabel?.(threadId) ?? 'Voice';
    return (
      <AttributedAssetsPane threadId={threadId} voiceLabel={voiceLabel} jwt={jwt} />
    );
  }

  // When an asset is open, the middle column becomes the asset editor.
  if (activeAssetId) {
    return (
      <AssetEditorPane
        assetId={activeAssetId}
        audit={audit}
        router={router}
        mode={mode}
        state={operatingModelState}
        refreshAudit={refreshAudit}
        jwt={jwt}
        onBackToChat={onCloseAsset}
        validationLocking={validationLockIn.locking}
        validationLockError={validationLockIn.lockError}
        onValidationLockIn={validationLockIn.onLockIn}
      />
    );
  }

  // Locked audit → activation moment layout.
  if (mode === 'locked') {
    return (
      <div data-testid="audit-chat" className="flex h-full flex-col overflow-y-auto px-6 py-4">
        {activationData ? (
          <ActivationMomentLayout {...activationData} />
        ) : (
          <p
            className="text-sm text-slate-500 italic"
            data-testid="activation-loading"
          >
            Preparing your activation summary…
          </p>
        )}
      </div>
    );
  }

  // System messages don't belong in the visible transcript.
  const visibleMessages = messages.filter((m) => m.role !== 'system');
  const appUrl = import.meta.env.VITE_VIBECONTROL_APP_URL ?? 'https://vibecontrol.app';
  const statusPanel =
    mode === 'materialized' ? (
      <div className="border-b border-line bg-[#07100d] px-6 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold text-emerald-200">AI team is live</p>
            <p className="mt-1 text-sm text-slate-400">
              Keep discussing changes here. RoleForge writes up every change for your approval before your team acts on it.
            </p>
          </div>
          <a
            href={appUrl}
            className="inline-flex items-center rounded-md border border-emerald-500/40 px-3 py-2 text-sm font-semibold text-emerald-100 hover:bg-emerald-500/10"
          >
            Open VibeControl
          </a>
        </div>
      </div>
    ) : mode === 'materializing' ? (
      <div className="border-b border-line bg-[#12171f] px-6 py-4">
        <p className="text-sm font-semibold text-slate-100">Setup is still running</p>
        <p className="mt-1 text-sm text-slate-400">
          Your Blueprint is saved. This page will stay on Step 4 while your AI team finishes being set up.
        </p>
      </div>
    ) : mode === 'failed' ? (
      <div className="border-b border-line bg-[#1d1114] px-6 py-4">
        <p className="text-sm font-semibold text-rose-200">Setup needs attention</p>
        <p className="mt-1 text-sm text-slate-400">
          Your Blueprint is still saved. Try setting up your AI team again, or contact support before continuing.
        </p>
      </div>
    ) : null;

  const proposalPanel =
    mode === 'materialized' && changeProposals.length > 0 ? (
      <div className="space-y-3 border-b border-line bg-paper px-6 py-4">
        {changeProposals.map((proposal) => (
          <div
            key={proposal.id}
            className="rounded-md border border-line bg-surface p-4"
            data-testid="change-proposal-card"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="text-sm font-semibold text-slate-100">{proposal.title}</p>
                <p className="mt-1 text-sm text-slate-400">{proposal.summary}</p>
              </div>
              <span className="rounded-full bg-slate-800 px-2 py-1 text-xs text-slate-300">
                {proposal.status.replace('_', ' ')}
              </span>
            </div>
            {proposal.status === 'proposed' && onApproveProposal && (
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="inline-flex min-h-[44px] items-center rounded-md border border-line px-3 py-2 text-sm font-medium text-ink hover:bg-card-hover focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral"
                  onClick={() => void onApproveProposal(proposal.id, 'later')}
                >
                  Create task
                </button>
                {/*
                  Issue #1216 (same defect class): `bg-primary` is not a colour
                  this app's Tailwind config defines, so this button rendered
                  with NO background and white text — invisible on the light
                  canvas. Coral with near-black text is 6.3:1 in both themes.
                */}
                <button
                  type="button"
                  className="inline-flex min-h-[44px] items-center rounded-md bg-coral px-3 py-2 text-sm font-semibold text-[#0B0E14] hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-coral"
                  onClick={() => void onApproveProposal(proposal.id, 'now')}
                >
                  Start now
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    ) : null;

  return (
    <div data-testid="audit-chat" className="relative flex h-full min-h-0 flex-col">
      {statusPanel}
      {proposalPanel}
      <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4">
        <ChatTranscript
          messages={visibleMessages}
          streamingAgentText={streamingAgentText}
          auditId={audit.id}
          activityItems={activityItems}
          agentSpeakerName={agentProfile?.name}
          agentAvatarUrl={agentProfile?.avatar_url}
        />
      </div>
      {mode === 'validating' && (
        <div className="border-t border-line bg-paper px-6 py-3">
          <p className="mb-2 text-xs text-coral">
            You're now reviewing my draft — confirm it when everything looks right.
          </p>
          <ValidationLockInAction
            audit={audit}
            jwt={jwt}
            refreshAudit={refreshAudit}
            locking={validationLockIn.locking}
            lockError={validationLockIn.lockError}
            onLockIn={validationLockIn.onLockIn}
          />
        </div>
      )}
      {onSend && (
        <div className="border-t border-line bg-paper px-6 py-3">
          <ChatComposer
            onSend={onSend}
            disabled={composerDisabled}
            placeholder={composerPlaceholder}
            transcribe={undefined}
          />
        </div>
      )}
      {synthesisBurstActive && (
        <ForgeSynthesisBurst router={router} onDone={onSynthesisBurstDone} />
      )}
    </div>
  );
}
